import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';

import { tagClassNames, filterColors, sorts, type TSort, fakeFilterHandler } from 'utils/filters';
import { useI18n } from 'context/i18n';
import { FilterTag } from 'components/filterTag';
import { getCountryFromMarket } from 'utils/translations';
import { useLocale } from 'hooks';
import { DisclosureGroup, Disclosure, DisclosureHeader, DisclosurePanel } from 'components/ui/disclosure';
import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { filterTransformer, getRealID } from 'utils/helpers';
import { createFilterMap, normalizeActiveFilters } from './use-filters';
import type { TFilter, TFilterSection } from 'types/product-list';
import type { FilterState } from './use-filters';

type CamperFilterPanelProps = {
  filters: TFilterSection[];
  state: FilterState;
  filtersCount: number;
  onApply: (filters: FilterState['active'], sort?: TSort) => void;
  onClose: (visible: boolean, fakeApply?: boolean) => void;
  onRemove: (params: { parent: string; key: string }) => void;
  onSort: (sort: TSort) => void;
  onReset: () => void;
};

export function CamperFilterPanel({ filters, state, filtersCount, onApply, onSort, onClose, onReset, onRemove }: CamperFilterPanelProps) {
  const { t } = useI18n();
  const { country: market } = useLocale();

  const country = getCountryFromMarket(market);

  const handleClickFilter = ({ parent, filter, multiple }: { parent: string; filter: TFilter; multiple: boolean }) => {
    const currentFilters = { ...state.active };
    // si no existe el parent lo creamos
    if (!currentFilters[parent]) {
      currentFilters[parent] = new Map<string, TFilter>();
    }
    if (!multiple) {
      // si no es multiple lo vaciamos y añadimos el filtro

      // si pulsa el mismo filtro que ya está aplicado, lo eliminamos
      if (currentFilters[parent].has(filter.key)) {
        currentFilters[parent].delete(filter.key);
      } else {
        if (currentFilters[parent].size > 0) {
          currentFilters[parent].clear();
        }

        currentFilters[parent].set(filter.key, filter);
      }
    }
    if (multiple) {
      // si es multiple, si ya existe el filtro lo eliminamos, si no lo añadimos
      if (currentFilters[parent].has(filter.key)) {
        currentFilters[parent].delete(filter.key);
      } else {
        currentFilters[parent].set(filter.key, filter);
      }
    }

    if (filter.url) {
      try {
        currentFilters[parent].delete(filter.key);

        if (currentFilters['collections']) {
          currentFilters['collections'].delete('rewa');
        }

        const urlFilters = filterTransformer.toQuery(normalizeActiveFilters(currentFilters));

        // miramos que el nuevo filtro tenga url, si es así mandamos a esa url con todos los filtros codificados a query params
        const url = new URL(filter.url);

        const searchParams = new URLSearchParams({
          ...urlFilters,
        });

        window.location.href = `${url.origin}${url.pathname}?${searchParams.toString()}`;
        return;
      } catch (e) {
        console.error('error', e);
      }
    }

    onApply(currentFilters, state.sort);
  };

  if (!state.open) {
    return null;
  }
  return (
    <div className="fixed inset-0 z-[100] flex min-h-0 flex-col overflow-hidden bg-[--background] pt-[--ribbon-height] md:static md:h-[calc(100vh-60px)] md:pt-0">
      <ScrollArea className="min-h-0 flex-grow overflow-hidden">
        <DisclosureGroup allowsMultipleExpanded className="h-full">
          <div className="flex flex-wrap gap-2 border-b border-subtle px-6 pb-5 pt-1 md:hidden">
            {Object.keys(state.active).map((filterKey) => {
              if (filterKey !== 'first') {
                return Array.from(state.active[filterKey] ?? []).map(([_, appliedFilter]) => (
                  <FilterTag
                    key={`${filterKey}.${appliedFilter.key}`}
                    onClick={() => {
                      onRemove({ parent: filterKey, key: appliedFilter.key });
                    }}
                    checked={true}
                    color={filterKey === 'colors' ? filterColors[appliedFilter.key as keyof typeof filterColors] : null}
                    label={appliedFilter.label !== '' ? appliedFilter.label : appliedFilter.urlParam}
                  />
                ));
              }
              return null;
            })}
          </div>
          {/* Applied Filters Panel (disabled, just for display) */}
          <Disclosure defaultExpanded className="md:border-t md:border-subtle">
            <DisclosureHeader buttonClassName="py-5 px-6 text-lg lg:text-lg">{t('menu.lateral', 'ordenar.por')}</DisclosureHeader>
            <DisclosurePanel className="flex flex-col gap-2 px-6">
              <ToggleButtonGroup className="flex flex-col items-start gap-4" selectedKeys={[state.sort]}>
                {Object.keys(sorts).map((sortValue) => (
                  <ToggleButton key={sortValue} id={sortValue} className="group flex items-center gap-2 px-2 py-1" onPress={() => onSort(sortValue as TSort)}>
                    <div className="flex size-4 items-center justify-center rounded-full border border-subtle group-selected:border-dark">
                      <div className="size-1.5 rounded-full group-selected:bg-neutral-inverted"></div>
                    </div>
                    {t('listado.productos', sorts[sortValue as keyof typeof sorts])}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </DisclosurePanel>
          </Disclosure>
          {/* Filter Sections */}
          {filters.map(({ title, id, options, multiple }) => {
            if (id === 'collections') {
              return null;
            }
            return (
              <Disclosure key={id} defaultExpanded={false}>
                <DisclosureHeader buttonClassName="py-5 px-6 text-lg lg:text-lg">
                  {id !== 'sizes' ? t(...title) : `${t(...title)} (${country})`}
                </DisclosureHeader>
                <DisclosurePanel data-sizes={id === 'sizes'} className="grid grid-cols-2 place-items-start gap-4 px-6 data-[sizes=true]:grid-cols-4">
                  {options.map((filter) => {
                    const checked = state.active[id] && state.active[id].has(filter.key);
                    const available = state.available[id] && state.available[id].has(filter.key);
                    return (
                      <FilterTag
                        key={filter.key}
                        onClick={() =>
                          handleClickFilter({
                            parent: id,
                            filter,
                            multiple,
                          })
                        }
                        checked={!!checked}
                        color={id === 'colors' ? filterColors[filter.key as keyof typeof filterColors] : null}
                        label={filter.label !== '' ? filter.label : filter.urlParam}
                        type={Object.keys(tagClassNames).includes(id) ? id : undefined}
                        available={!!available}
                      />
                    );
                  })}
                  {/* <List
                    grid={filterGrid[id] || filterGrid.default}
                    dataSource={options}
                    locale={{ emptyText: 'No available filters' }}
                    renderItem={(filter) => {
                      const checked = state.active[id] && state.active[id].has(filter.key);
                      const available = state.available[id] && state.available[id].has(filter.key);
                      return (
                        <List.Item key={filter.key}>
                          <div
                          // className={styles.customFilterItem}
                          >
                            <FilterTag
                              onClick={() =>
                                handleClickFilter({
                                  parent: id,
                                  filter,
                                  multiple,
                                })
                              }
                              checked={checked}
                              color={id === 'colors' ? filterColors[filter.key] : null}
                              label={filter.label !== '' ? filter.label : filter.urlParam}
                              type={Object.keys(tagClassNames).includes(id) ? id : undefined}
                              available={available}
                            />
                          </div>
                        </List.Item>
                      );
                    }}
                  /> */}
                </DisclosurePanel>
              </Disclosure>
            );
          })}
        </DisclosureGroup>
      </ScrollArea>
      <div className="grid grid-cols-2 gap-4 bg-clear px-6 py-5 md:hidden">
        <Button variant="outline" onPress={() => onClose(false)}>
          {t('generico', 'boton.cerrar', 'Close')}
        </Button>
        <Button variant="outline" onPress={onReset}>
          {t('generico', 'boton.reiniciar', 'Reset')}
        </Button>
        <Button onPress={() => onClose(false, true)}>{t('generico', 'boton.aplicar', 'Apply')}</Button>
      </div>
    </div>
  );
}
